// frontend/components/sidebar/docPermission/PermissionTabs.tsx
//
// 권한 설정 모달의 "팀" / "유저" 탭 테이블을 렌더링합니다.

import { Lock } from "lucide-react";
import PermSelect from "./PermSelect";
import { PERMISSION_SOURCE_LABELS } from "../../../utils/documentStatus";

// 일반 권한 콤보박스 — 전체허용 없는 4단계
export type Perm4 = "NONE" | "READ" | "COMMENT" | "EDIT";

export interface PermUserData {
  id: string;
  name?: string;
  loginId: string;
  department?: string;
  role?: string;
}

export type EffectivePermission = { level: Perm4 | "FULL" | null; source: string };

export const PERM_OPTIONS: { value: Perm4; label: string }[] = [
  { value: "NONE",    label: "권한 없음" },
  { value: "READ",    label: "읽기 허용" },
  { value: "COMMENT", label: "댓글 허용" },
  { value: "EDIT",    label: "편집 허용" },
];

// ── 팀 탭 ────────────────────────────────────────────────────────────────────

export function TeamTab({
  depts, primaryDept, teamPerms, onChange,
}: {
  depts: string[];
  primaryDept: string;
  teamPerms: Record<string, Perm4>;
  onChange: (dept: string, level: Perm4) => void;
}) {
  return (
    <table className="w-full text-sm border-collapse">
      <thead className="sticky top-0 z-10">
        <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-700">
          <th className="text-left text-xs font-semibold text-gray-500 dark:text-zinc-400 px-5 py-2.5 w-full">팀</th>
          <th className="text-right text-xs font-semibold text-gray-500 dark:text-zinc-400 px-5 py-2.5 whitespace-nowrap">권한</th>
        </tr>
      </thead>
      <tbody>
        {depts.map(dept => {
          const isPrimary = dept === primaryDept;
          const level = teamPerms[dept] ?? (isPrimary ? "EDIT" : "NONE");
          return (
            <tr
              key={dept}
              className={`border-b border-gray-50 dark:border-zinc-800/50 transition-colors ${
                isPrimary
                  ? "bg-green-50/40 dark:bg-green-900/10"
                  : "hover:bg-gray-50 dark:hover:bg-zinc-800/40"
              }`}
            >
              <td className="px-5 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-gray-800 dark:text-zinc-100 font-medium">{dept}</span>
                  {isPrimary && (
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 border border-green-500 dark:border-green-600 text-green-600 dark:text-green-400 leading-none"
                      style={{ borderRadius: 0 }}
                    >
                      생성팀
                    </span>
                  )}
                </div>
              </td>
              <td className="px-5 py-3 text-right">
                {isPrimary ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
                    <Lock size={11} />
                    편집 허용
                  </span>
                ) : (
                  <PermSelect
                    value={level}
                    options={PERM_OPTIONS}
                    onChange={v => onChange(dept, v as Perm4)}
                  />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── 유저 탭 ──────────────────────────────────────────────────────────────────

export function UserTab({
  users, creatorUser, primaryDept, userPerms, effectivePermissions, onChange,
}: {
  users: PermUserData[];
  creatorUser: PermUserData | null;
  primaryDept: string;
  userPerms: Record<string, Perm4>;
  effectivePermissions: Record<string, EffectivePermission>;
  onChange: (userId: string, level: Perm4) => void;
}) {
  if (users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-gray-400">
        사용자 정보 없음
      </div>
    );
  }

  return (
    <table className="w-full text-sm border-collapse">
      <thead className="sticky top-0 z-10">
        <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-700">
          <th className="text-left text-xs font-semibold text-gray-500 dark:text-zinc-400 px-5 py-2.5 w-full">이름</th>
          <th className="text-right text-xs font-semibold text-gray-500 dark:text-zinc-400 px-5 py-2.5 whitespace-nowrap">권한</th>
        </tr>
      </thead>
      <tbody>
        {users.map(u => {
          const isCreator = u.id === creatorUser?.id;
          const level: Perm4 | "FULL" = isCreator
            ? "FULL"
            : (userPerms[u.id] ?? (u.department === primaryDept ? "EDIT" : "NONE"));
          const effective = effectivePermissions[u.id];

          return (
            <tr
              key={u.id}
              className={`border-b border-gray-50 dark:border-zinc-800/50 transition-colors ${
                isCreator
                  ? "bg-blue-50/40 dark:bg-blue-900/10"
                  : "hover:bg-gray-50 dark:hover:bg-zinc-800/40"
              }`}
            >
              <td className="px-5 py-2.5">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-gray-800 dark:text-zinc-100 font-medium">
                    {u.name || u.loginId}
                  </span>
                  {isCreator && (
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 border border-blue-500 dark:border-blue-600 text-blue-600 dark:text-blue-400 leading-none"
                      style={{ borderRadius: 0 }}
                    >
                      생성자
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 dark:text-zinc-500">
                  {u.department || "-"} · {PERMISSION_SOURCE_LABELS[effective?.source || "NONE"]}
                </p>
              </td>
              <td className="px-5 py-2.5 text-right">
                {isCreator ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400">
                    <Lock size={11} />
                    전체 허용
                  </span>
                ) : (
                  <PermSelect
                    value={level as Perm4}
                    options={PERM_OPTIONS}
                    onChange={v => onChange(u.id, v as Perm4)}
                  />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
